import React from "react";
import { Filter, SlidersHorizontal, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsTablet } from "@/hooks/use-tablet";
import { cn } from "@/lib/utils";

interface ResponsiveFilterToolbarProps {
  children: React.ReactNode;
  actionButtons?: React.ReactNode;
  className?: string;
  title?: string;
  hasActiveFilters?: boolean;
  activeFiltersCount?: number;
  onShowResults?: () => void;
  showButtonText?: string;
  onResetFilters?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  resetButtonText?: string;
  showButtonDisabled?: boolean;
}

export const ResponsiveFilterToolbar: React.FC<ResponsiveFilterToolbarProps> = ({
  children,
  actionButtons,
  className,
  title = "Filters",
  hasActiveFilters = false,
  activeFiltersCount,
  onShowResults,
  showButtonText = "Show Results",
  onResetFilters,
  resetButtonText = "Reset Filters",
  showButtonDisabled = false,
}) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);

  // Handle sheet open/close to prevent automatic keyboard opening
  const handleSheetOpenChange = React.useCallback(
    (open: boolean) => {
      setIsSheetOpen(open);

      if ((isMobile || isTablet) && open) {
        setTimeout(() => {
          if (document.activeElement && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }, 100);
      }
    },
    [isMobile, isTablet]
  );

  // Don't render anything until device detection is complete
  if (isMobile === undefined || isTablet === undefined) {
    return (
      <div className={cn("flex items-center justify-between gap-2 mt-4 mb-4", className)}>
        <div className="h-10 w-24 bg-muted animate-pulse rounded-md"></div>
        {actionButtons && <div className="flex items-center gap-2">{actionButtons}</div>}
      </div>
    );
  }

  // For mobile and tablet devices, show filters in a sheet/drawer
  if (isMobile === true || isTablet === true) {
    return (
      <div className={cn("flex items-center justify-between gap-2 mt-4 mb-4", className)}>
        <Sheet open={isSheetOpen} onOpenChange={handleSheetOpenChange}>
          <SheetTrigger asChild>
            <div className="relative">
              <Button
                variant={hasActiveFilters ? "default" : "outline"}
                size="sm"
                className={cn("h-10 gap-2", hasActiveFilters && "bg-primary text-primary-foreground")}
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span>{title}</span>
              </Button>
              {hasActiveFilters && (
                <Badge
                  variant="destructive"
                  className="absolute -top-2 -right-2 min-w-5 h-5 px-1 text-xs font-bold z-10"
                >
                  {activeFiltersCount || "•"}
                </Badge>
              )}
            </div>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-full flex flex-col">
            <SheetHeader className="shrink-0">
              <SheetTitle className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                {title}
              </SheetTitle>
              <SheetDescription>Use the filters below to refine your search and find specific items.</SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              <div
                className="space-y-4"
                onClick={(e) => {
                  // Prevent keyboard from opening when clicking on the container
                  if (e.target === e.currentTarget) {
                    e.preventDefault();
                  }
                }}
              >
                {children}
              </div>
            </div>
            {(onShowResults || onResetFilters) && (
              <div className="flex items-center gap-2 p-4 border-t bg-background shrink-0">
                {onResetFilters && (
                  <Button variant="outline" size="sm" className="flex-1 h-11" onClick={onResetFilters}>
                    {resetButtonText}
                  </Button>
                )}
                {onShowResults && (
                  <SheetClose asChild>
                    <Button
                      variant="default"
                      size="sm"
                      className="flex-1 h-11 gap-2"
                      onClick={onShowResults}
                      disabled={showButtonDisabled}
                    >
                      <Eye className="h-4 w-4" />
                      {showButtonText}
                    </Button>
                  </SheetClose>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>
        {actionButtons && <div className="flex items-center gap-2">{actionButtons}</div>}
      </div>
    );
  }

  // For desktop devices, show the original horizontal layout
  return (
    <div className={cn("flex items-center justify-between gap-2 mt-4 mb-4", className)}>
      <div className="flex items-center space-x-4 ml-1">
        {children}
        {onResetFilters && (
          <Button variant="outline" onClick={onResetFilters}>
            {resetButtonText}
          </Button>
        )}
      </div>
      <div className="flex items-center space-x-2">{actionButtons}</div>
    </div>
  );
};

// Mobile-specific filter item wrapper for better spacing and sizing
export const MobileFilterItem: React.FC<{
  children: React.ReactNode;
  label?: string;
  preventAutoFocus?: boolean;
}> = ({ children, label, preventAutoFocus = true }) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Clone children and add mobile-specific props to prevent auto-focus
  const processedChildren = React.useMemo(() => {
    if ((isMobile === true || isTablet === true) && preventAutoFocus) {
      return React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // Add props to prevent auto-focus on mobile inputs
          const childProps = child.props as any;
          return React.cloneElement(child, {
            autoFocus: false,
            onFocus: (e: React.FocusEvent) => {
              if (childProps?.onFocus) {
                childProps.onFocus(e);
              }
            },
            ...childProps,
          });
        }
        return child;
      });
    }
    return children;
  }, [children, isMobile, isTablet, preventAutoFocus]);

  if ((isMobile === true || isTablet === true) && label) {
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium">{label}</label>
        <div className="w-full">{processedChildren}</div>
      </div>
    );
  }

  return <>{processedChildren}</>;
};

// Hook to get responsive styles for filter components
export const useResponsiveFilterStyles = () => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  return {
    inputStyles: isMobile === true || isTablet === true ? "h-10 w-full" : "h-9 w-[150px] lg:w-[250px]",
    buttonStyles: isMobile === true || isTablet === true ? "h-10 w-full justify-start" : "h-9",
    containerStyles: isMobile === true || isTablet === true ? "flex-col space-y-4" : "space-x-4",
  };
};

// Utility hook to check if filters have active values
export const useActiveFilters = (filters: Record<string, any>) => {
  return React.useMemo(() => {
    const activeFilters = Object.entries(filters).filter(([key, value]) => {
      const isActive =
        value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && value.length === 0);
      return isActive;
    });

    const result = {
      hasActiveFilters: activeFilters.length > 0,
      activeFiltersCount: activeFilters.length,
      activeFilterKeys: activeFilters.map(([key]) => key),
    };

    return result;
  }, [filters]);
};
